/**
 * Pedidos de autorización de trabajo, del lado de core (change
 * `partners-por-horas`, tareas 5.3 y 4.3): esquema del formulario, armado del
 * pedido, qué acciones tiene cada pedido según su estado y textos de la lista.
 * Todo puro. La aritmética del presupuesto vive en `autorizaciones.ts`.
 */
import { z } from 'zod';

import type { Database } from '@/types/database.types';
import {
  aCentesimas,
  decidirPublicacionEnMinutos,
  horasDisponibles,
  totalPresupuesto,
  type DecisionEnMinutos,
  type EstadoAutorizacion,
  type MonedaAutorizacion,
} from './autorizaciones';
import type { Modalidad, ValorHora } from './por-horas';

export type Autorizacion = Database['core']['Tables']['work_authorizations']['Row'];

/**
 * Recargo de una urgencia fuera de horario cuando el partner no tiene uno
 * propio (decisión de Denis, 14/10). El vigente es `tenants.urgent_surcharge`
 * (change `urgencias-y-excedente-con-firma`, D1).
 */
export const RECARGO_URGENCIA = 1.5;
export const RECARGO_NORMAL = 1;

export const ETIQUETA_ESTADO: Record<EstadoAutorizacion, string> = {
  pendiente: 'Pendiente de firma',
  autorizada: 'Autorizada',
  rechazada: 'Rechazada',
  anulada: 'Anulada',
};

export const TONO_ESTADO: Record<EstadoAutorizacion, 'warn' | 'ok' | 'err' | 'default'> = {
  pendiente: 'warn',
  autorizada: 'ok',
  rechazada: 'err',
  anulada: 'default',
};

export function esEstadoDeAutorizacion(s: string): s is EstadoAutorizacion {
  return s === 'pendiente' || s === 'autorizada' || s === 'rechazada' || s === 'anulada';
}

// ── Formulario ─────────────────────────────────────────────────────────────

/** `numeric(10,2)`: a lo sumo dos decimales. */
function dosDecimales(n: number): boolean {
  return Math.abs(n * 100 - Math.round(n * 100)) < 1e-6;
}

// ── Plan de seguimiento (change `trabajo-autorizado`) ──────────────────────

const PREFIJO_PORTAL = 'portal:';
const PREFIJO_SISTEMA = 'sistema:';

/**
 * Cómo se paga el trabajo (decisión de Denis, 23/10, `authorized_works.plan`):
 * `descuenta_del_plan` usa horas del abono y no se cobra aparte; `fuera_del_plan`
 * se cobra aparte. Un partner por horas solo puede `fuera_del_plan`.
 */
export type PlanDePago = 'descuenta_del_plan' | 'fuera_del_plan';

export const ETIQUETA_PLAN_DE_PAGO: Record<PlanDePago, string> = {
  descuenta_del_plan: 'Descuenta del plan',
  fuera_del_plan: 'Fuera del plan',
};

export function esPlanDePago(s: unknown): s is PlanDePago {
  return s === 'descuenta_del_plan' || s === 'fuera_del_plan';
}

/**
 * Cómo se va a seguir el trabajo al firmarse. Vive dentro del formulario del
 * pedido para que el borrador lo incluya. `ticket_ref` lleva el canal con
 * prefijo (`portal:<id>` o `sistema:<id>`), como el selector del pedido.
 */
export const planSchema = z
  .object({
    project_id: z.string().nullable(),
    etapa: z.enum(['nueva', 'existente']),
    phase_id: z.string().nullable(),
    ticket: z.enum(['nuevo', 'existente']),
    ticket_ref: z.string().nullable(),
    pricing: z.enum(['cerrado', 'por_horas']),
    /** Cómo se paga: obligatorio. Un partner por horas arranca (y queda) en «Fuera del plan». */
    plan: z.enum(['descuenta_del_plan', 'fuera_del_plan']).nullish(),
    contrato: z.enum(['sin_anexo', 'anexo_existente', 'anexo_nuevo']),
    contract_item_id: z.string().nullable(),
    contract_id: z.string().nullable(),
  })
  .superRefine((p, ctx) => {
    const falta = (path: string, message: string) => ctx.addIssue({ code: 'custom', path: [path], message });
    if (!p.plan) falta('plan', 'Elegí cómo se paga el trabajo');
    // Sin proyecto no hay plan: el aviso del diálogo ofrece vincular o crear uno.
    if (!p.project_id) return;
    if (p.etapa === 'existente' && !p.phase_id) falta('phase_id', 'Elegí la etapa');
    if (p.ticket === 'existente' && !p.ticket_ref) falta('ticket_ref', 'Elegí el ticket');
    if (p.contrato === 'anexo_existente' && !p.contract_item_id) falta('contract_item_id', 'Elegí el anexo');
    if (p.contrato === 'anexo_nuevo' && !p.contract_id) falta('contract_id', 'Elegí el contrato');
  });

export type PlanFormValues = z.infer<typeof planSchema>;

/**
 * El plan de un pedido nuevo: etapa y ticket nuevos, presupuesto cerrado, sin anexo. El plan de
 * pago no tiene valor por defecto para un abonado (lo decide Denis caso a caso); un partner por
 * horas va siempre «Fuera del plan».
 */
export function planPorDefecto(projectId: string | null, porHoras = false): PlanFormValues {
  return {
    project_id: projectId,
    etapa: 'nueva',
    phase_id: null,
    ticket: 'nuevo',
    ticket_ref: null,
    pricing: 'cerrado',
    plan: porHoras ? 'fuera_del_plan' : null,
    contrato: 'sin_anexo',
    contract_item_id: null,
    contract_id: null,
  };
}

/** Lo que se escribe en `core.authorized_works`. */
export type FilaDePlan = {
  project_id: string;
  phase_id: string | null;
  new_phase: boolean;
  open_ticket: boolean;
  pricing: 'cerrado' | 'por_horas';
  plan: PlanDePago;
  contract_plan: 'sin_anexo' | 'anexo_existente' | 'anexo_nuevo';
  contract_item_id: string | null;
  contract_id: string | null;
};

/** El ticket existente del plan (`authorized_work_tickets`, `source = 'pedido'`). */
export type TicketDelPlan = { portal_ticket_id: string | null; client_ticket_id: string | null };

export type PlanArmado = { fila: FilaDePlan; ticket: TicketDelPlan | null };

/**
 * Arma lo que se escribe. Sin proyecto, o con el plan incompleto, no hay plan (null). Con
 * `porHoras` el plan de pago es «Fuera del plan» pase lo que pase (la base lo exige).
 */
export function armarPlanDeTrabajo(
  plan: PlanFormValues | null | undefined,
  opciones: { porHoras?: boolean } = {},
): PlanArmado | null {
  if (!plan) return null;
  const p = planSchema.parse(opciones.porHoras ? { ...plan, plan: 'fuera_del_plan' } : plan);
  if (!p.project_id) return null;
  const refTicket = p.ticket === 'existente' ? p.ticket_ref : null;
  return {
    fila: {
      project_id: p.project_id,
      phase_id: p.etapa === 'existente' ? p.phase_id : null,
      new_phase: p.etapa === 'nueva',
      open_ticket: p.ticket === 'nuevo',
      pricing: p.pricing,
      plan: p.plan ?? 'fuera_del_plan',
      contract_plan: p.contrato,
      contract_item_id: p.contrato === 'anexo_existente' ? p.contract_item_id : null,
      contract_id: p.contrato === 'anexo_nuevo' ? p.contract_id : null,
    },
    ticket: refTicket
      ? refTicket.startsWith(PREFIJO_SISTEMA)
        ? { portal_ticket_id: null, client_ticket_id: refTicket.slice(PREFIJO_SISTEMA.length) }
        : { portal_ticket_id: refTicket.slice(PREFIJO_PORTAL.length), client_ticket_id: null }
      : null,
  };
}

/** Vuelve a los valores del formulario a partir de lo guardado. */
export function planDesdeGuardado(fila: FilaDePlan, ticket: TicketDelPlan | null): PlanFormValues {
  return {
    project_id: fila.project_id,
    etapa: fila.new_phase ? 'nueva' : fila.phase_id ? 'existente' : 'nueva',
    phase_id: fila.new_phase ? null : fila.phase_id,
    ticket: fila.open_ticket || !ticket ? 'nuevo' : 'existente',
    ticket_ref: ticket?.client_ticket_id
      ? PREFIJO_SISTEMA + ticket.client_ticket_id
      : ticket?.portal_ticket_id
        ? PREFIJO_PORTAL + ticket.portal_ticket_id
        : null,
    pricing: fila.pricing,
    plan: fila.plan,
    contrato: fila.contract_plan,
    contract_item_id: fila.contract_item_id,
    contract_id: fila.contract_id,
  };
}

export const autorizacionSchema = z.object({
  title: z.string().trim().min(1, 'Poné un título').max(200, 'Máximo 200 caracteres'),
  scope: z.string().trim().min(1, 'Contá qué incluye el trabajo').max(5000, 'Máximo 5000 caracteres'),
  estimated_hours: z
    .number({ message: 'Tiene que ser un número' })
    .positive('Tiene que ser mayor que cero')
    .max(10000, 'Demasiadas horas')
    .refine(dosDecimales, 'Hasta dos decimales'),
  urgente: z.boolean(),
  client_ticket_id: z.string().nullable(),
  /** Ticket del portal asociado (excluyente con el pedido del sistema). */
  ticket_id: z.string().nullable(),
  /** Cómo se sigue el trabajo al firmarse. Ausente en un excedente: hereda el de la raíz. */
  plan: planSchema.nullish(),
});

export type AutorizacionFormValues = z.infer<typeof autorizacionSchema>;

export const VALORES_INICIALES: AutorizacionFormValues = {
  title: '',
  scope: '',
  estimated_hours: 1,
  urgente: false,
  client_ticket_id: null,
  ticket_id: null,
};

/** El recargo de un pedido: el de urgencia del partner si es urgente, 1 si no. */
export function recargoDe(urgente: boolean, recargoUrgencia: number = RECARGO_URGENCIA): number {
  return urgente ? recargoUrgencia : RECARGO_NORMAL;
}

/**
 * Horas de plan que descuenta un pedido en un partner con abono: horas × recargo,
 * a dos decimales (D3 del change del recargo). 2 h urgentes ×1,5 = 3 h.
 */
export function horasQueDescuenta(horas: number, recargo: number): number {
  return Math.round(aCentesimas(horas) * Math.round(recargo * 100) / 100) / 100;
}

/** Valores del formulario para editar un pedido pendiente. */
export function valoresDeAutorizacion(a: Autorizacion): AutorizacionFormValues {
  return {
    title: a.title,
    scope: a.scope,
    estimated_hours: Number(a.estimated_hours),
    urgente: Number(a.surcharge) > RECARGO_NORMAL,
    client_ticket_id: a.client_ticket_id,
    ticket_id: a.ticket_id,
  };
}

/** Total que se va a firmar, para mostrarlo mientras se escribe. */
export function totalDelFormulario(
  v: Pick<AutorizacionFormValues, 'estimated_hours' | 'urgente'>,
  valorHora: number,
  /** Recargo fijo (excedente): manda sobre la urgencia del formulario. */
  recargo?: number,
  /** El recargo de urgencia del partner (o el que ya tiene el pedido). */
  recargoUrgencia?: number,
): number | null {
  if (!Number.isFinite(v.estimated_hours) || v.estimated_hours <= 0) return null;
  return totalPresupuesto(v.estimated_hours, valorHora, recargo ?? recargoDe(v.urgente, recargoUrgencia));
}

/** Lo que se escribe al pedir una autorización. El total NO viaja: lo calcula la base. */
export type NuevoPedido = {
  tenant_id: string;
  title: string;
  scope: string;
  estimated_hours: number;
  hour_price: number;
  surcharge: number;
  currency: MonedaAutorizacion;
  client_ticket_id: string | null;
  ticket_id: string | null;
  parent_id?: string;
  /** Modalidad con la que se pide: `abono` para urgencias y excedentes de un abono. */
  billing_mode?: Modalidad;
};

/**
 * Arma el pedido. El valor de la hora lo pone el partner (el catálogo): no hay
 * campo para escribirlo a mano. Sin valor, no hay pedido.
 */
export function armarPedido(
  tenantId: string,
  values: AutorizacionFormValues,
  valor: ValorHora | null | undefined,
  /** Excedente: cuelga de la autorización raíz y hereda su recargo. */
  extra: { parentId?: string; recargo?: number; recargoUrgencia?: number; modalidad?: Modalidad } = {},
): NuevoPedido {
  if (!valor) {
    throw new Error('Este partner no tiene valor de hora. Usá «Actualizar precios» en Planes habilitados.');
  }
  const v = autorizacionSchema.parse(values);
  return {
    tenant_id: tenantId,
    title: v.title,
    scope: v.scope,
    estimated_hours: v.estimated_hours,
    hour_price: valor.precio,
    surcharge: extra.recargo ?? recargoDe(v.urgente, extra.recargoUrgencia),
    currency: valor.moneda,
    client_ticket_id: v.client_ticket_id,
    ticket_id: v.ticket_id,
    ...(extra.parentId ? { parent_id: extra.parentId } : {}),
    billing_mode: extra.modalidad ?? 'por_horas',
  };
}

/** Cambios de un pedido pendiente: el valor de la hora y la moneda NO se tocan. */
export type CambiosDePedido = Omit<
  NuevoPedido,
  'tenant_id' | 'hour_price' | 'currency' | 'parent_id' | 'surcharge' | 'billing_mode'
> & {
  /** Ausente en un excedente: hereda el recargo de la autorización raíz. */
  surcharge?: number;
};

export function armarCambios(
  values: AutorizacionFormValues,
  opts: { esExcedente?: boolean; recargoUrgencia?: number } = {},
): CambiosDePedido {
  const v = autorizacionSchema.parse(values);
  return {
    title: v.title,
    scope: v.scope,
    estimated_hours: v.estimated_hours,
    // Un excedente conserva el recargo de la raíz: el formulario no lo muestra.
    ...(opts.esExcedente ? {} : { surcharge: recargoDe(v.urgente, opts.recargoUrgencia) }),
    client_ticket_id: v.client_ticket_id,
    ticket_id: v.ticket_id,
  };
}

// ── Qué se puede hacer con cada pedido ─────────────────────────────────────

export interface AccionesDeAutorizacion {
  editar: boolean;
  anular: boolean;
  avisar: boolean;
  verConstancia: boolean;
  generarConstancia: boolean;
}

/**
 * Las acciones de un pedido según su estado. Pendiente: se edita, se anula y se
 * avisa al cliente. Autorizada: constancia (verla si existe, generarla si la
 * firma quedó sin PDF). Rechazada: verla solo si tiene. Anulada: ninguna.
 */
export function accionesDeAutorizacion(
  a: Pick<Autorizacion, 'status' | 'constancia_path'>,
): AccionesDeAutorizacion {
  const tiene = Boolean(a.constancia_path?.trim());
  const pendiente = a.status === 'pendiente';
  return {
    editar: pendiente,
    anular: pendiente,
    avisar: pendiente,
    verConstancia: (a.status === 'autorizada' || a.status === 'rechazada') && tiene,
    generarConstancia: a.status === 'autorizada' && !tiene,
  };
}

/** Las autorizaciones asociadas a un pedido del sistema del cliente o a un ticket del portal. */
export function pedidosDeAutorizacionDelPedido(
  lista: readonly Autorizacion[],
  pedido: { clientTicketId?: string; ticketId?: string },
): Autorizacion[] {
  return lista.filter(
    (a) =>
      (pedido.clientTicketId !== undefined && a.client_ticket_id === pedido.clientTicketId) ||
      (pedido.ticketId !== undefined && a.ticket_id === pedido.ticketId),
  );
}

// ── Textos de la lista ─────────────────────────────────────────────────────

/** «Ciudad, Región, País» a partir de `signer_location`; null si no hay nada. */
export function textoDeUbicacion(loc: unknown): string | null {
  if (!loc || typeof loc !== 'object' || Array.isArray(loc)) return null;
  const o = loc as Record<string, unknown>;
  const pick = (...claves: string[]) => {
    for (const k of claves) {
      const v = o[k];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
    return null;
  };
  const partes = [pick('city', 'ciudad'), pick('region', 'regionName', 'provincia'), pick('country', 'pais')]
    .filter((p): p is string => p !== null)
    .filter((p, i, todas) => todas.indexOf(p) === i);
  return partes.length > 0 ? partes.join(', ') : null;
}

/** «1,5 h» / «6 h»: hasta dos decimales, coma decimal. */
export function textoDeHoras(h: number | string): string {
  const n = Number(h);
  const t = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 2 }).format(n);
  return `${t} h`;
}

export function textoDeMonto(n: number | string, moneda: string): string {
  const t = new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n));
  return `${moneda} ${t}`;
}

/** «×1,50»; null si no hay recargo. */
export function textoDeRecargo(r: number | string): string | null {
  const n = Number(r);
  if (!(n > RECARGO_NORMAL)) return null;
  return `×${new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)}`;
}

// ── Publicar contra una autorización (5.4) ─────────────────────────────────

/** Una autorización como la devuelve `core.portal_autorizaciones` (con lo publicado). */
export interface AutorizacionConConsumo {
  id: string;
  parent_id: string | null;
  billing_mode: string;
  title: string;
  estimated_hours: number;
  hour_price: number;
  surcharge: number;
  currency: string;
  status: string;
  client_ticket_id: string | null;
  ticket_id: string | null;
  /** Horas de entradas publicadas imputadas a ESTE pedido (no a su pozo). */
  horas_publicadas: number;
}

/** Una autorización raíz con sus excedentes: las horas se miden sobre el conjunto. */
export interface Pozo {
  madre: AutorizacionConConsumo;
  excedentes: AutorizacionConConsumo[];
  autorizadas: number;
  publicadas: number;
  disponibles: number;
  /** Minutos publicados contra el conjunto (la unidad con que decide el portal). */
  publicadasMin: number;
}

const comoHoras = (a: AutorizacionConConsumo) => ({
  estado: (esEstadoDeAutorizacion(a.status) ? a.status : 'pendiente') as EstadoAutorizacion,
  horas: a.estimated_hours,
});

/** Las raíces de una modalidad (con o sin saldo) con sus excedentes y lo ya publicado en el conjunto. */
export function pozosDeAutorizaciones(
  lista: readonly AutorizacionConConsumo[],
  modalidad: Modalidad = 'por_horas',
): Pozo[] {
  return lista
    .filter((a) => a.parent_id === null && a.billing_mode === modalidad)
    .map((madre) => {
      const excedentes = lista.filter((e) => e.parent_id === madre.id);
      const publicadas = [madre, ...excedentes].reduce((s, a) => s + aCentesimas(a.horas_publicadas), 0) / 100;
      const h = horasDisponibles(comoHoras(madre), excedentes.map(comoHoras), publicadas);
      // `horas_publicadas` viene redondeada a centésimas: al minuto entero más cercano se recupera exacto.
      const publicadasMin = [madre, ...excedentes].reduce((s, a) => s + Math.round(a.horas_publicadas * 60), 0);
      return { madre, excedentes, ...h, publicadasMin };
    });
}

/** Las que se pueden elegir al publicar: autorizadas y con horas por usar. */
export function pozosConSaldo(pozos: readonly Pozo[]): Pozo[] {
  return pozos.filter((p) => p.madre.status === 'autorizada' && p.disponibles > 0);
}

/** Cuál proponer: la ya imputada a la entrada, la del mismo pedido, o la única con saldo. */
export function pozoInicial(
  pozos: readonly Pozo[],
  entrada: { authorization_id?: string | null; client_ticket_id?: string | null; ticket_id?: string | null },
): string | null {
  const delConjunto = (id: string | null | undefined) =>
    pozos.find((p) => p.madre.id === id || p.excedentes.some((e) => e.id === id))?.madre.id ?? null;
  const imputada = delConjunto(entrada.authorization_id);
  if (imputada) return imputada;
  const delPedido = pozos.filter(
    (p) =>
      (entrada.client_ticket_id && p.madre.client_ticket_id === entrada.client_ticket_id) ||
      (entrada.ticket_id && p.madre.ticket_id === entrada.ticket_id),
  );
  if (delPedido.length === 1) return delPedido[0].madre.id;
  return pozos.length === 1 ? pozos[0].madre.id : null;
}

/** ¿Se puede publicar una entrada de `minutos` contra este conjunto? Cuenta en minutos, como el portal. */
export function decisionDePublicacion(pozo: Pozo | null | undefined, minutos: number): DecisionEnMinutos {
  if (!pozo) return { tipo: 'sin_autorizacion' };
  return decidirPublicacionEnMinutos(
    comoHoras(pozo.madre),
    pozo.excedentes.map(comoHoras),
    pozo.publicadasMin,
    minutos,
  );
}

/** Las horas a pedir de excedente para cubrir `faltanMin` minutos (hacia arriba, dos decimales). */
export function horasParaCubrir(faltanMin: number): number {
  return Math.ceil((faltanMin / 60) * 100) / 100;
}

/** El pedido del excedente: mismo valor, moneda y recargo que la autorización raíz. */
export function pedidoDeExcedente(
  madre: AutorizacionConConsumo,
  horasFaltantes: number,
  ticket: { client_ticket_id?: string | null; ticket_id?: string | null } = {},
): { prefill: Partial<AutorizacionFormValues>; valor: ValorHora; recargo: number; parentId: string } {
  return {
    prefill: {
      title: `Excedente: ${madre.title}`.slice(0, 200),
      scope: `Horas que exceden lo autorizado en «${madre.title}».`,
      estimated_hours: Math.round(horasFaltantes * 100) / 100,
      urgente: madre.surcharge > RECARGO_NORMAL,
      client_ticket_id: ticket.client_ticket_id ?? madre.client_ticket_id,
      ticket_id: ticket.ticket_id ?? madre.ticket_id,
    },
    valor: { precio: madre.hour_price, moneda: madre.currency as MonedaAutorizacion },
    recargo: madre.surcharge,
    parentId: madre.id,
  };
}
